import {
  CellClassParams,
  ColDef,
  ISetFilterParams,
} from '@ag-grid-community/core'
import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { HeliosColumnTypes } from '@helios/ag-grid-config'
import { FilterModalService } from '@helios/filter-modal'
import { RootState } from '@helios/state-management'
import {
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { Store } from '@ngrx/store'
import { NfColumnTypes } from '@northfork/ag-grid'
import {
  composeFieldPath,
  formatDate,
  ISODateTimeFormat,
} from '@northfork/basics'
import { BehaviorSubject } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
} from 'rxjs/operators'
import { ApprovalsKey } from '../../../panel-key'
import { getCellStyleForRequestStatus } from '../../models/approvals-util'
import { Requests, RestrictedBreakDetail } from '../../models/approvals.model'
import { ApprovalsService } from '../../services/approvals.service'
import { RequestDetailGridBase } from './request-detail-grid-base'

const RestrictedBreakDetailGridKey = `${ApprovalsKey}-RestrictedBreakDetailGrid`

@Component({
  selector: 'hs-request-detail-grid-restricted-breaks',
  templateUrl: './request-detail-grid.component.html',
  styleUrls: ['./request-detail-grid.component.scss'],
  providers: [FilterModalService],
})
export class RequestDetailGridRestrictedBreaksComponent
  extends RequestDetailGridBase<RestrictedBreakDetail>
  implements OnInit, OnDestroy
{
  protected gridKey = RestrictedBreakDetailGridKey
  field = composeFieldPath(this.data)
  approvalDetailGridOptions = this.createGridOptions()
  idsSubject$ = new BehaviorSubject<number[]>([])

  constructor(
    protected readonly preferences: UserPreferencesService,
    protected readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly approvalsService: ApprovalsService,
    protected readonly store: Store<RootState>,
    public filterModalService: FilterModalService,
  ) {
    super(preferences, savedViewService, store, filterModalService)
  }

  @Input() set refreshRequestDetails(
    selectedRequestDetails: RestrictedBreakDetail[] | undefined,
  ) {
    this.approvalDetailGridOptions = this.createGridOptions()
    const selectedRequestIds = selectedRequestDetails?.map(num => num.requestId)
    if (selectedRequestIds && selectedRequestIds?.length > 0) {
      this.isLoading = true
      this.approvalsService
        .getRequestDetailsByType<RestrictedBreakDetail>(
          selectedRequestIds,
          'RESTRICTED_BREAK',
        )
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(details => {
          this.data = details.requests
          this.isLoading = false
          this.refreshRequest.emit(true)
        })
    } else {
      this.refreshRequest.emit(false)
    }
  }

  @Input() set selectedRequest(selectedRequest: Requests[] | undefined) {
    this.approvalDetailGridOptions = this.createGridOptions()
    const selectedRequestIds = selectedRequest?.map(num => num.requestId)
    if (selectedRequestIds && selectedRequestIds?.length > 0) {
      this.loadRequestDetails(selectedRequestIds)
    }
  }

  ngOnInit() {
    super.ngOnInit()
    this.idsSubject$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroySubject$),
        switchMap((ids: number[]) => {
          if (ids) {
            this.isLoading = true
            return this.approvalsService.getRequestDetailsByType<RestrictedBreakDetail>(
              ids,
              'RESTRICTED_BREAK',
            )
          }
          return []
        }),
        takeUntil(this.destroySubject$),
      )
      .subscribe(details => {
        this.data = details?.requests || []
        this.isLoading = false
      })
  }

  loadRequestDetails(ids: number[]) {
    this.isLoading = true
    this.idsSubject$.next(ids)
  }

  ngOnDestroy(): void {
    super.ngOnDestroy()
  }

  protected hasAccountId(row: RestrictedBreakDetail): boolean {
    return row.accountId != null
  }

  protected getAccountId(row: RestrictedBreakDetail): number {
    return row.accountId
  }

  createColumnDefs(): ColDef[] {
    return [
      {
        field: this.field('requestedBy'),
        headerName: 'Username',
        filter: 'agSetColumnFilter',
        filterParams: {
          defaultToNothingSelected: true,
          values: params => {
            this.approvalsService
              .getUserNames()
              .subscribe(values => params.success(values))
          },
          closeOnApply: true,
        } as ISetFilterParams,
      },
      {
        field: this.field('requestId'),
        headerName: 'Request ID',
        type: NfColumnTypes.Integer,
      },
      {
        field: this.field('actionType'),
        headerName: 'Action Type',
      },
      {
        field: this.field('accountId'),
        headerName: 'Account ID',
        type: NfColumnTypes.Identifier,
      },
      {
        field: this.field('ultimateParentId'),
        headerName: 'Ult Parent Client',
        type: NfColumnTypes.Identifier,
      },
      {
        field: this.field('reconDate'),
        headerName: 'Recon Date',
        type: NfColumnTypes.Date,
      },
      {
        field: this.field('breakType'),
        headerName: 'Break Type',
        filter: 'agSetColumnFilter',
      },
      {
        field: this.field('assetId'),
        headerName: 'Asset ID',
      },
      {
        field: this.field('breakId'),
        headerName: 'Break ID',
      },
      {
        field: this.field('status'),
        headerName: 'Request Status',
        cellStyle: (params: CellClassParams) => {
          return getCellStyleForRequestStatus(params)
        },
        filter: 'agSetColumnFilter',
      },
      {
        field: this.field('reviewer'),
        headerName: 'Reviewed By',
        filter: 'agSetColumnFilter',
      },
      {
        field: this.field('comment'),
        headerName: 'Justification',
      },
      {
        field: this.field('createdAt'),
        headerName: 'Created TS',
        type: HeliosColumnTypes.DateTime,
        valueFormatter: params =>
          formatDate(params.value, 'MM/dd/yyyy HH:mm', ISODateTimeFormat) ??
          params.value,
      },
      {
        field: this.field('actionTimestamp'),
        headerName: 'Review TS',
        type: HeliosColumnTypes.DateTime,
        valueFormatter: params =>
          formatDate(params.value, 'MM/dd/yyyy HH:mm', ISODateTimeFormat) ?? '',
      },
      {
        field: this.field('reviewComment'),
        headerName: 'Reviewer Comment',
      },
    ]
  }
}
